import { convertChannelCasing } from 'lib-shared-api-utils';
import { ConversationFormValues } from 'lib-shared-legacy-types';
import { Channel } from 'lib-shared-types-channels';
import { ComponentProps } from 'react';
import { connect } from 'react-redux';
import { getFormValues, InjectedFormProps } from 'redux-form';

import { aiApi, ReadAiWritingGuideMatchApiArg } from 'apis/ai';
import { appActions } from 'app/app.actions';
import { bindRtkActionToPromise } from 'app/utils/promiseActions';
import { AppDispatch, AppState } from 'bootstrap.store';
import { AddUserPayload, userModalActions } from 'components/usersModal';
import { RouteComponentProps, withRouter } from 'hocs/router';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { withServerStorage } from 'utils/useServerStorage';

import { websocketActions } from '../../../app/middleware/websocket/actions/websocket';
import { notificationActions } from '../../notifications/actions/notification';
import * as personalActions from '../../settings/actions/personal';
import { templatesActions } from '../../settings/actions/templates';
import { composeEmailActions } from '../../shared/composeEmail/actions/composeEmailActions';
import { hasFeature } from '../../shared/featureFlag';
import { actions as conversationActions } from '../actions/conversation';
import { actions as conversationsActions } from '../actions/conversations';
import * as typingActions from '../actions/typing';
import ConversationForm from '../components/ConversationForm';
import { getLastEndUserMessage } from '../selectors';
import { getConversationFormName } from '../utils/conversationForm';

const { personalSubmitMessageWithEnter } = personalActions.actions;
const { typingSend } = typingActions.actions;

const { notificationAdd } = notificationActions;

const mapStateToProps = (state) => {
  const metaConversation = state.conversation.metaConversation;
  const fromEmails = state.composeEmail.fromEmails;
  const csid = metaConversation.conversation.id;
  const form = getConversationFormName(csid);
  const formValues = getFormValues(form)(state);
  const tempFormValues = state.conversation.metaConversation.tempFormValues;

  const tempMessage =
    state.conversations.metaConversations.find(
      (mConversation) => mConversation.conversation.id === csid,
    )?.tempMessage || null;

  return {
    asyncStatus: state.conversation.asyncStatus,
    composeEmailMeta: state.composeEmail.meta,
    form,
    formValues,
    tempFormValues,
    fromEmails,
    getAllFromAsyncStatus: state.composeEmail.meta.getAllFrom,
    hasFFReplyAll: hasFeature(state, 'reply-all-toggle-and-config'),
    hasFFWriteHTMLEmail: hasFeature(state, 'write-html-email'),
    hasFFWhatsappRichMessages: hasFeature(
      state,
      'conversation-whatsapp-rich-messages',
    ),
    lastEndUserMessage:
      state.conversation.metaConversation && getLastEndUserMessage(state),
    metaConversation: state.conversation.metaConversation,
    tempMessage,
    profile: state.settings.profile.profile,
    personalSettings: state.settings.personal,
    users: state.composeEmail.users,
    organizationName: state.settings.organization.organization.name,
    elevioSmartReplyData: (state as AppState).elevio.aiResponse.data,
    knowledgeSmartReply: state.knowledge.smartConversationReply,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    composeEmailFindUser: bindActionToPromise(
      dispatch,
      composeEmailActions.composeEmailFindUser,
    ),
    composeEmailGetAllFrom: bindActionToPromise(
      dispatch,
      composeEmailActions.composeEmailGetAllFrom,
    ),
    conversationUnmount: () =>
      dispatch(conversationActions.conversationUnmount()),
    saveTempMessage: (
      csid: string,
      message: {
        text: string;
        contentType: 'html' | 'markdown';
      } | null,
    ) =>
      dispatch(
        conversationsActions.conversationsSaveTempMessage({
          csid: csid,
          message: message,
        }),
      ),
    conversationSaveTempFormValues: (
      csid: number,
      formValues: ConversationFormValues | null,
    ) =>
      dispatch(
        conversationActions.conversationSaveTempFormValues({
          csid,
          formValues,
        }),
      ),
    conversationSubmitMessage: bindActionToPromise(
      dispatch,
      conversationActions.conversationSubmitMessage,
    ),
    notificationAdd: (payload) => dispatch(notificationAdd(payload)),
    personalSubmitMessageWithEnter: bindActionToPromise(
      dispatch,
      personalSubmitMessageWithEnter,
    ),
    templatesRender: bindActionToPromise(
      dispatch,
      templatesActions.templatesRender,
    ),
    typingSend: bindActionToPromise(dispatch, typingSend),
    userModalAddUser: (payload: AddUserPayload) =>
      dispatch(userModalActions.user.add(payload)),
    wsRequest: bindRtkActionToPromise(dispatch, websocketActions.request),
    showPublicReplyConfirmModal: () => {
      return dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: 'Public reply',
              description:
                'Replying to a mention will make your response public. Remember to make sure that the response is appropriate for a public forum.',
              confirm: { label: 'Yes, send now', variant: 'primary' },
            },
          },
        }),
      );
    },
    hasStyleGuideForChannel: async (channel: Channel) => {
      const promise = dispatch(
        aiApi.endpoints.readAIWritingStyleGuideMatch.initiate({
          features: 'impr_reply',
          ...(channel && {
            channels: convertChannelCasing(channel, 'snake') as Channel,
          }),
        } as ReadAiWritingGuideMatchApiArg),
      );

      const { data: styleGuide } = await promise;

      promise.unsubscribe();

      return !!styleGuide?.data?.match;
    },
  };
};

type ConversationFormStateProps = ReturnType<typeof mapStateToProps>;
type ConversationFormDispatchProps = ReturnType<typeof mapDispatchToProps>;

export type ConversationFormInjectedProps = ConversationFormStateProps &
  ConversationFormDispatchProps &
  InjectedFormProps &
  RouteComponentProps;

const ConversationFormHookAdapter = (
  props: ComponentProps<typeof ConversationForm>,
): JSX.Element => {
  const { hasCopilot } = useCopilotTrial();

  return <ConversationForm hasCopilot={hasCopilot} {...props} />;
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(
    withServerStorage({
      scope: 'organization',
      propName: 'organizationStorage',
    })(ConversationFormHookAdapter),
  ),
);
